import React from 'react';
import { Card, Space, Button, Tag, Typography } from 'antd';
import { EyeOutlined, EditOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Case, CaseStatus } from '../types';
import './CaseCardView.css';

const { Text } = Typography;

interface CaseCardViewProps {
  cases: Case[];
  loading: boolean;
  onView: (caseItem: Case) => void;
  onEdit: (caseItem: Case) => void;
}

const CaseCardView: React.FC<CaseCardViewProps> = ({
  cases,
  loading,
  onView,
  onEdit,
}) => {
  const getStatusColor = (status: CaseStatus) => {
    const colors: Record<CaseStatus, string> = {
      [CaseStatus.PREPARING_FILING]: 'default',
      [CaseStatus.FILED]: 'cyan',
      [CaseStatus.SUPPLEMENTING]: 'gold',
      [CaseStatus.MEDIATION]: 'purple',
      [CaseStatus.HEARING]: 'blue',
      [CaseStatus.AWAITING_VERDICT]: 'geekblue',
      [CaseStatus.FIRST_INSTANCE]: 'processing',
      [CaseStatus.SECOND_INSTANCE]: 'processing',
      [CaseStatus.RETRIAL]: 'orange',
      [CaseStatus.ENFORCEMENT]: 'magenta',
      [CaseStatus.ACTIVE]: 'processing',
      [CaseStatus.CLOSED]: 'default',
      [CaseStatus.SUSPENDED]: 'warning',
      [CaseStatus.MEDIATION_CLOSED]: 'success',
      [CaseStatus.VERDICT_CLOSED]: 'success',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: CaseStatus) => {
    const texts: Record<CaseStatus, string> = {
      [CaseStatus.PREPARING_FILING]: '准备立案材料',
      [CaseStatus.FILED]: '立案',
      [CaseStatus.SUPPLEMENTING]: '补充材料',
      [CaseStatus.MEDIATION]: '调解',
      [CaseStatus.HEARING]: '开庭',
      [CaseStatus.AWAITING_VERDICT]: '等待裁决书',
      [CaseStatus.FIRST_INSTANCE]: '一审',
      [CaseStatus.SECOND_INSTANCE]: '二审',
      [CaseStatus.RETRIAL]: '再审',
      [CaseStatus.ENFORCEMENT]: '执行',
      [CaseStatus.ACTIVE]: '进行中',
      [CaseStatus.CLOSED]: '已结案',
      [CaseStatus.SUSPENDED]: '已暂停',
      [CaseStatus.MEDIATION_CLOSED]: '调解结案',
      [CaseStatus.VERDICT_CLOSED]: '裁决结案',
    };
    return texts[status] || status;
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>加载中...</div>;
  }

  if (cases.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>暂无数据</div>;
  }

  return (
    <div className="case-card-container">
      {cases.map(caseItem => (
        <Card key={caseItem.id} className="case-card" onClick={() => onView(caseItem)}>
          <div className="case-card-header">
            <Text strong style={{ fontSize: 16 }}>{caseItem.case_name}</Text>
            <Tag color={getStatusColor(caseItem.case_status)}>{getStatusText(caseItem.case_status)}</Tag>
          </div>

          <div className="case-card-body">
            {caseItem.case_type && (
              <div className="case-card-item">
                <Text type="secondary">类型：</Text>
                <Text>{caseItem.case_type}</Text>
              </div>
            )}

            {caseItem.responsible_lawyer_name && (
              <div className="case-card-item">
                <Text type="secondary">负责律师：</Text>
                <Text>{caseItem.responsible_lawyer_name}</Text>
              </div>
            )}

            {caseItem.court_date && (
              <div className="case-card-item">
                <CalendarOutlined style={{ color: '#faad14' }} />
                <Text>开庭：{dayjs(caseItem.court_date).format('MM-DD HH:mm')}</Text>
              </div>
            )}

            {caseItem.accept_date && (
              <div className="case-card-item">
                <Text type="secondary">承接：{dayjs(caseItem.accept_date).format('YYYY-MM-DD')}</Text>
              </div>
            )}
          </div>

          <div className="case-card-footer" onClick={(e) => e.stopPropagation()}>
            <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => onView(caseItem)}>
              查看
            </Button>
            <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(caseItem)}>
              编辑
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default CaseCardView;
